import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { FiX, FiExternalLink, FiMessageSquare, FiArrowRight } from "react-icons/fi";
import { formatRelativeTime } from "../../Helper/dates";
import { iconForContentType } from "../../Pages/Publication/publicationUtils";
import { UserAvatar } from "../UserAvatar";
import { ticketsClient, type Ticket, type TicketAuthor, type TicketComment } from "../../Pages/Tickets/ticketsClient";

/** Small localized status pill (same tone as the Tickets page). */
const StatusPill: React.FC<{ status: string }> = ({ status }) =>
	status === "closed" ? (
		<span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">Fermé</span>
	) : (
		<span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-600">Ouvert</span>
	);

/** Compact author chip (small avatar + name), used for comments and assignees. */
const AuthorChip: React.FC<{ author?: TicketAuthor | null; fallback?: string | null }> = ({ author, fallback }) => {
	const name = author?.name || fallback || "Système";
	return (
		<span className="inline-flex items-center gap-1.5 align-middle">
			<UserAvatar name={name} id={author?.id ?? undefined} avatarKey={author?.avatarKey} pictureUrl={author?.pictureUrl} sizeClass="h-5 w-5" textClass="text-[9px]" />
			<span className="font-semibold text-slate-600">{name}</span>
		</span>
	);
};

/**
 * Read-only ticket detail shown as a modal from within a content/page editor, so an editor can inspect a
 * linked ticket WITHOUT leaving the document they are working on. Deliberately non-editable : status,
 * assignees, linked contents and comments are all display-only; managing the ticket happens on the
 * Tickets page (a discreet link is offered). Fetches the full ticket + comments on open.
 */
export const TicketDetailModal: React.FC<{
	ticketId: string | null;
	categoryLabel?: (key: string) => string;
	onClose: () => void;
}> = ({ ticketId, categoryLabel, onClose }) => {
	const [data, setData] = useState<{ ticket: Ticket; comments: TicketComment[] } | null>(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!ticketId) {
			setData(null);
			return;
		}
		let alive = true;
		setLoading(true);
		ticketsClient
			.get(ticketId)
			.then((d) => alive && setData(d))
			.catch(() => alive && setData(null))
			.finally(() => alive && setLoading(false));
		return () => {
			alive = false;
		};
	}, [ticketId]);

	if (!ticketId) return null;

	const ticket = data?.ticket;
	const creatorName = ticket?.createdBy?.name ?? ticket?.createdByEmail ?? "Système";
	const catLabel = (key?: string) => (key ? categoryLabel?.(key) ?? key : "");

	return createPortal(
		<div className="fixed inset-0 z-[1300] flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
			<div className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
				<div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
					<h2 className="text-base font-bold text-slate-800">Détail du ticket</h2>
					<button type="button" onClick={onClose} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600" title="Fermer">
						<FiX />
					</button>
				</div>

				<div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
					{loading && !ticket ? (
						<p className="py-10 text-center text-sm text-slate-400">Chargement…</p>
					) : !ticket ? (
						<p className="py-10 text-center text-sm text-slate-400">Ticket introuvable.</p>
					) : (
						<div className="space-y-4">
							{/* Header: bigger creator avatar (clearly visible) + title + status/category */}
							<div className="flex items-start gap-3">
								<UserAvatar
									name={creatorName}
									id={ticket.createdBy?.id ?? undefined}
									avatarKey={ticket.createdBy?.avatarKey}
									pictureUrl={ticket.createdBy?.pictureUrl}
									sizeClass="h-11 w-11"
									textClass="text-sm"
									title={`Créé par ${creatorName}`}
								/>
								<div className="min-w-0 flex-1">
									<div className="flex flex-wrap items-center gap-2">
										<StatusPill status={ticket.status} />
										<span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">{catLabel(ticket.category)}</span>
									</div>
									<h3 className="mt-1.5 text-lg font-bold leading-snug text-slate-900">{ticket.title}</h3>
									<p className="mt-0.5 text-xs text-slate-400">
										Ouvert par <span className="font-semibold text-slate-600">{creatorName}</span> · {formatRelativeTime(ticket.createdAt)}
									</p>
								</div>
							</div>

							{ticket.description && (
								<p className="whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-sm text-slate-700">{ticket.description}</p>
							)}

							{/* Linked contents : read-only cards (a click deliberately opens that content). */}
							{(ticket.linkedContents?.length ?? 0) > 0 && (
								<div>
									<p className="mb-1 text-xs font-semibold text-slate-600">Contenu(s) lié(s)</p>
									<div className="grid gap-1.5 sm:grid-cols-2">
										{ticket.linkedContents!.map((c) => {
											const Icon = iconForContentType(c.title, c.contentType);
											return (
												<Link
													key={c.contentId}
													to={`/content/${encodeURIComponent(c.contentType ?? "")}/${encodeURIComponent(c.contentId)}`}
													className="group flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 transition hover:border-sky-300 hover:bg-sky-50"
													title={c.title ?? c.contentId}
												>
													<span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-500">
														<Icon className="h-3.5 w-3.5" />
													</span>
													<span className="min-w-0 flex-1">
														<span className="block truncate text-sm font-semibold text-slate-800">{c.title || c.contentId}</span>
														{c.contentType && <span className="block truncate text-[10px] uppercase tracking-wide text-slate-400">{c.contentType}</span>}
													</span>
													<FiExternalLink className="h-3.5 w-3.5 shrink-0 text-slate-300 group-hover:text-sky-600" />
												</Link>
											);
										})}
									</div>
								</div>
							)}

							{/* Assignees : read-only avatars + names. */}
							<div>
								<p className="mb-1 text-xs font-semibold text-slate-600">Assignés</p>
								{(ticket.assigneesResolved?.length ?? ticket.assignees.length) === 0 ? (
									<p className="text-sm text-slate-400">Non assigné.</p>
								) : (
									<div className="flex flex-wrap gap-2">
										{(ticket.assigneesResolved && ticket.assigneesResolved.length > 0
											? ticket.assigneesResolved.map((a) => ({ key: a.id ?? a.email ?? a.name, author: a as TicketAuthor | null, fallback: a.email }))
											: ticket.assignees.map((e) => ({ key: e, author: null as TicketAuthor | null, fallback: e }))
										).map((a) => (
											<span key={a.key} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-600">
												<AuthorChip author={a.author} fallback={a.fallback} />
											</span>
										))}
									</div>
								)}
							</div>

							{/* Comments : read-only. */}
							<div>
								<p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-slate-600">
									<FiMessageSquare className="h-3.5 w-3.5" /> Commentaires
								</p>
								<ul className="space-y-2">
									{(data?.comments.length ?? 0) === 0 ? (
										<li className="text-sm text-slate-400">Aucun commentaire.</li>
									) : (
										data!.comments.map((c) => (
											<li key={c.id} className="rounded-lg border border-slate-100 bg-slate-50 p-2.5">
												<div className="flex items-center gap-2 text-[11px] text-slate-500">
													<AuthorChip author={c.author} fallback={c.authorEmail} />
													<span>· {formatRelativeTime(c.createdAt)}</span>
												</div>
												<p className="mt-0.5 whitespace-pre-wrap text-sm text-slate-700">{c.body}</p>
											</li>
										))
									)}
								</ul>
							</div>
						</div>
					)}
				</div>

				{/* Managing the ticket (edit, assign, comment) happens on the Tickets page : not inline here. */}
				<div className="flex items-center justify-end border-t border-slate-100 px-5 py-3">
					<Link
						to={`/tickets?ticket=${encodeURIComponent(ticketId)}`}
						className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3.5 py-2 text-sm font-semibold text-white hover:bg-slate-700"
					>
						Gérer dans Tickets <FiArrowRight className="h-4 w-4" />
					</Link>
				</div>
			</div>
		</div>,
		document.body
	);
};

export default TicketDetailModal;
